import { useState, useEffect, useMemo } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Archive, RotateCcw, Search, Mail, Phone, Shield, Eye, EyeOff, FileText } from 'lucide-react';

import { UserRecapSheet } from './UserRecapSheet';

const ROLE_LABELS = {
  admin: 'Admin',
  gestionnaire: 'Gestion',
  formateur: 'Formateur',
  eleve: 'Élève',
  apporteur: 'Apporteur',
};

const ROLE_TINTS = {
  admin: 'bg-[#1F2A4A] text-[#E8DCC8]',
  gestionnaire: 'bg-[#C9B896]/30 text-[#1F2A4A]',
  formateur: 'bg-emerald-500/15 text-emerald-700',
  eleve: 'bg-[#1F2A4A]/8 text-[#1F2A4A]',
  apporteur: 'bg-amber-500/15 text-amber-700',
};

export const UserManagementAdmin = () => {
  const [users, setUsers] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [recapUserId, setRecapUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listUsers(includeArchived);
      setUsers(data || []);
    } catch (_) { toast.error("Erreur lors du chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(q),
    );
  }, [users, search]);

  const handleArchive = async (u) => {
    if (!window.confirm(`Archiver "${u.first_name} ${u.last_name}" ?\n\nL'utilisateur ne pourra plus se connecter mais ses données restent conservées.`)) return;
    try {
      await api.archiveUser(u.id);
      toast.success("Utilisateur archivé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleRestore = async (u) => {
    try {
      await api.restoreUser(u.id);
      toast.success("Utilisateur réactivé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="user-management-admin">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">UTILISATEURS</p>
          <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A]">
            Comptes <span className="font-serif italic text-[#C9B896]">de la plateforme.</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F2A4A]/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (nom, email)..."
              className="pl-10 w-64 bg-transparent border-[#1F2A4A]/15"
              data-testid="user-search"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setIncludeArchived((v) => !v)}
            className="rounded-full"
            data-testid="user-toggle-archived"
          >
            {includeArchived ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {includeArchived ? 'Masquer archivés' : 'Voir archivés'}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#1F2A4A]/40 text-sm">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-sm text-[#1F2A4A]/40 italic">Aucun utilisateur trouvé.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1F2A4A]/10 bg-[#FAF7F0]/50 text-xs uppercase tracking-wider text-[#1F2A4A]/50 font-mono">
                <th className="text-left px-6 py-3">Utilisateur</th>
                <th className="text-left px-3 py-3">Contact</th>
                <th className="text-left px-3 py-3">Rôle</th>
                <th className="text-left px-3 py-3">Statut</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const archived = !!u.is_archived;
                return (
                  <tr key={u.id} className={`border-b border-[#1F2A4A]/8 last:border-0 transition-colors ${archived ? 'bg-[#1F2A4A]/4 opacity-70' : 'hover:bg-[#FAF7F0]/40'}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-[#1F2A4A]">
                        {u.first_name} {u.last_name}
                      </div>
                      <div className="text-[10px] text-[#1F2A4A]/40 font-mono">{u.id.slice(0, 8)}</div>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="text-[#1F2A4A]/80 flex items-center gap-1.5"><Mail className="w-3 h-3 text-[#C9B896]" /> {u.email}</div>
                      {u.phone && <div className="text-[#1F2A4A]/60 flex items-center gap-1.5 mt-1"><Phone className="w-3 h-3 text-[#C9B896]" /> {u.phone}</div>}
                    </td>
                    <td className="px-3 py-4">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-medium ${ROLE_TINTS[u.role] || 'bg-[#1F2A4A]/8 text-[#1F2A4A]/70'}`}>
                        <Shield className="w-3 h-3" /> {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      {archived ? (
                        <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-red-500/10 text-red-600 font-medium">Archivé</span>
                      ) : (
                        <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-700 font-medium">Actif</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => setRecapUserId(u.id)}
                          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#1F2A4A] hover:text-[#C9B896] transition-colors"
                          data-testid={`user-recap-${u.id}`}
                          title="Voir la fiche client complète"
                        >
                          <FileText className="w-3.5 h-3.5" /> Fiche
                        </button>
                      {archived ? (
                        <button
                          onClick={() => handleRestore(u)}
                          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#1F2A4A] hover:text-[#C9B896] transition-colors"
                          data-testid={`user-restore-${u.id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Restaurer
                        </button>
                      ) : (
                        <button
                          onClick={() => handleArchive(u)}
                          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-red-500 hover:text-red-700 transition-colors"
                          data-testid={`user-archive-${u.id}`}
                        >
                          <Archive className="w-3.5 h-3.5" /> Archiver
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <UserRecapSheet userId={recapUserId} onClose={() => setRecapUserId(null)} />
    </div>
  );
};

export default UserManagementAdmin;
